import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled, { keyframes } from 'styled-components'
import { theme } from '../styles/theme'
import { useSettings } from '../contexts/SettingsContext'
import logoMark from '../assets/logo-mark.png'

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-10px); }
`

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`

// ─── Layout ───────────────────────────────────────────────────────────────────

const Page = styled.div<{ $rtl: boolean }>`
  min-height: 100vh;
  background: ${theme.colors.background};
  font-family: ${theme.typography.fontFamily.primary};
  direction: ${p => (p.$rtl ? 'rtl' : 'ltr')};
  color: ${theme.colors.text};
`

// ─── Navbar ───────────────────────────────────────────────────────────────────

const Nav = styled.nav`
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 48px;
  height: 68px;
  background: rgba(255, 249, 238, 0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid ${theme.colors.border};

  @media (max-width: 640px) {
    padding: 0 20px;
  }
`

const NavLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text};
  text-decoration: none;
`

const HexLogo = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
`

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const LangSelect = styled.select`
  background: ${theme.colors.light};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.medium};
  padding: 6px 10px;
  font-size: 13px;
  color: ${theme.colors.text};
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: ${theme.colors.primary};
  }
`

const NavGetStartedBtn = styled.button`
  background: transparent;
  color: ${theme.colors.primaryDark};
  border: 1.5px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.medium};
  padding: 8px 20px;
  font-size: 14px;
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  &:hover {
    background: rgba(246, 180, 0, 0.08);
  }
`

const NavLoginBtn = styled.button`
  background: ${theme.colors.primary};
  color: ${theme.colors.dark};
  border: none;
  border-radius: ${theme.borderRadius.medium};
  padding: 8px 20px;
  font-size: 14px;
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(246, 180, 0, 0.4);
  }
`

// ─── Hero ─────────────────────────────────────────────────────────────────────

const Hero = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 80px 24px 64px;
  animation: ${fadeUp} 0.6s ease both;
`

const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(246, 180, 0, 0.12);
  border: 1px solid rgba(246, 180, 0, 0.3);
  border-radius: ${theme.borderRadius.full};
  padding: 6px 16px;
  font-size: 13px;
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.primaryDark};
  margin-bottom: 24px;
`

const HeroTitle = styled.h1`
  font-size: clamp(36px, 6vw, 64px);
  font-weight: ${theme.typography.fontWeight.bold};
  line-height: 1.15;
  color: ${theme.colors.text};
  max-width: 760px;
  margin: 0 0 20px;

  span {
    background: linear-gradient(120deg, ${theme.colors.primary}, ${theme.colors.primaryDark}, ${theme.colors.honey});
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 3s linear infinite;
  }
`

const HeroSub = styled.p`
  font-size: 18px;
  color: ${theme.colors.textSecondary};
  max-width: 520px;
  line-height: 1.7;
  margin: 0 0 40px;
`

const HeroCTA = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryDark});
  color: ${theme.colors.dark};
  border: none;
  border-radius: ${theme.borderRadius.large};
  padding: 16px 40px;
  font-size: 17px;
  font-weight: ${theme.typography.fontWeight.bold};
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  box-shadow: 0 4px 20px rgba(246, 180, 0, 0.35);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(246, 180, 0, 0.5);
  }

  &:active {
    transform: translateY(0);
  }
`

const HeroEmoji = styled.div`
  font-size: 80px;
  margin: 48px 0 0;
  animation: ${float} 4s ease-in-out infinite;
`

// ─── Stats ────────────────────────────────────────────────────────────────────

const StatsBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 0;
  flex-wrap: wrap;
  background: ${theme.colors.white};
  border-top: 1px solid ${theme.colors.border};
  border-bottom: 1px solid ${theme.colors.border};
  animation: ${fadeUp} 0.7s ease both;
`

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 48px;
  border-right: 1px solid ${theme.colors.border};

  &:last-child { border-right: none; }

  @media (max-width: 640px) {
    padding: 20px 24px;
    border-right: none;
    border-bottom: 1px solid ${theme.colors.border};
    width: 50%;
  }
`

const StatNumber = styled.div`
  font-size: 32px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary};
  line-height: 1;
`

const StatLabel = styled.div`
  font-size: 13px;
  color: ${theme.colors.textSecondary};
  margin-top: 4px;
`

// ─── Features ─────────────────────────────────────────────────────────────────

const FeaturesSection = styled.section`
  padding: 80px 24px;
  max-width: 1100px;
  margin: 0 auto;
  animation: ${fadeUp} 0.8s ease both;
`

const SectionLabel = styled.div`
  text-align: center;
  font-size: 13px;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.primaryDark};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 12px;
`

const SectionTitle = styled.h2`
  text-align: center;
  font-size: clamp(26px, 4vw, 40px);
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text};
  margin: 0 0 16px;
`

const SectionSub = styled.p`
  text-align: center;
  font-size: 16px;
  color: ${theme.colors.textSecondary};
  max-width: 560px;
  margin: 0 auto 56px;
  line-height: 1.7;
`

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
`

const FeatureCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.xlarge};
  padding: 32px;
  transition: all ${theme.transitions.normal};

  &:hover {
    border-color: ${theme.colors.primary};
    box-shadow: 0 8px 32px rgba(246, 180, 0, 0.12);
    transform: translateY(-4px);
  }
`

const FeatureIcon = styled.div`
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, rgba(246, 180, 0, 0.15), rgba(246, 180, 0, 0.05));
  border-radius: ${theme.borderRadius.large};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  margin-bottom: 20px;
`

const FeatureTitle = styled.h3`
  font-size: 18px;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text};
  margin: 0 0 10px;
`

const FeatureDesc = styled.p`
  font-size: 14px;
  color: ${theme.colors.textSecondary};
  line-height: 1.65;
  margin: 0;
`

// ─── Workflow ─────────────────────────────────────────────────────────────────

const WorkflowSection = styled.section`
  background: ${theme.colors.white};
  border-top: 1px solid ${theme.colors.border};
  border-bottom: 1px solid ${theme.colors.border};
  padding: 80px 24px;
`

const WorkflowInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
`

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 48px;
`

const StepCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 28px 20px;
  background: ${theme.colors.background};
  border-radius: ${theme.borderRadius.xlarge};
  border: 1px solid ${theme.colors.borderLight};
`

const StepNum = styled.div`
  width: 36px;
  height: 36px;
  background: ${theme.colors.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${theme.typography.fontWeight.bold};
  font-size: 16px;
  color: ${theme.colors.dark};
  margin-bottom: 14px;
`

const StepTitle = styled.div`
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: 15px;
  color: ${theme.colors.text};
  margin-bottom: 8px;
`

const StepDesc = styled.div`
  font-size: 13px;
  color: ${theme.colors.textSecondary};
  line-height: 1.6;
`

// ─── CTA Section ──────────────────────────────────────────────────────────────

const CTASection = styled.section`
  padding: 80px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: ${fadeUp} 0.9s ease both;
`

const CTATitle = styled.h2`
  font-size: clamp(28px, 4vw, 44px);
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text};
  margin: 0 0 16px;
`

const CTASub = styled.p`
  font-size: 16px;
  color: ${theme.colors.textSecondary};
  max-width: 480px;
  line-height: 1.7;
  margin: 0 0 36px;
`

// ─── Footer ───────────────────────────────────────────────────────────────────

const Footer = styled.footer`
  border-top: 1px solid ${theme.colors.border};
  padding: 24px 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  color: ${theme.colors.textSecondary};

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 8px;
    padding: 20px;
    text-align: center;
  }
`

// ─── Language configs ─────────────────────────────────────────────────────────

const LANGUAGES = [
  { code: 'en', label: '🇺🇸 English', rtl: false },
  { code: 'he', label: '🇮🇱 עברית',   rtl: true  },
  { code: 'ar', label: '🇸🇦 العربية', rtl: true  },
  { code: 'es', label: '🇪🇸 Español', rtl: false },
  { code: 'th', label: '🇹🇭 ภาษาไทย', rtl: false },
]

// ─── Landing content per language ────────────────────────────────────────────

type LandingContent = {
  badge: string
  heroTitle: string
  heroTitleHighlight: string
  heroSub: string
  cta: string
  statsLabel1: string
  statsLabel2: string
  statsLabel3: string
  statsLabel4: string
  /** Honey-yield stat value — carries a localized weight unit */
  statsValue4: string
  featuresLabel: string
  featuresTitle: string
  featuresSub: string
  features: { icon: string; title: string; desc: string }[]
  workflowLabel: string
  workflowTitle: string
  workflowSub: string
  steps: { title: string; desc: string }[]
  ctaTitle: string
  ctaSub: string
  ctaBtn: string
  footerTagline: string
  loginBtn: string
  getStarted: string
}

const CONTENT: Record<string, LandingContent> = {
  en: {
    badge: '🐝 Smart Beekeeping Platform',
    heroTitle: 'Manage Your Apiaries ',
    heroTitleHighlight: 'The Smart Way',
    heroSub: 'Real-time monitoring, AI-powered recommendations, and complete hive management — all in one platform.',
    cta: 'Enter the System',
    statsLabel1: 'Hives Tracked',
    statsLabel2: 'Active Users',
    statsLabel3: 'Inspections',
    statsLabel4: 'Honey Yield Tracked',
    statsValue4: '1M kg',
    featuresLabel: 'Everything you need',
    featuresTitle: 'Built for Professional Beekeepers',
    featuresSub: 'From a single hive to hundreds of apiaries — Hivenetic scales with your operation.',
    features: [
      { icon: '🏡', title: 'Yard Management', desc: 'Track every apiary location with GPS coordinates, gate codes, and visit history.' },
      { icon: '🐝', title: 'Hive Monitoring', desc: 'Log inspections, colony health, population stats, and mortality rates per hive.' },
      { icon: '👥', title: 'Team Coordination', desc: 'Assign tasks to field teams, track progress, and manage schedules effortlessly.' },
      { icon: '🍯', title: 'Harvest & Inventory', desc: 'Record honey extractions by type, manage warehouse stock and box inventory.' },
      { icon: '🤖', title: 'AI Recommendations', desc: 'Get smart action suggestions based on inspection data and seasonal patterns.' },
      { icon: '📋', title: 'ELAP Reports', desc: 'Generate ELAP-compliant loss event reports and eligibility assessments automatically.' },
    ],
    workflowLabel: 'How it works',
    workflowTitle: 'From Field to Dashboard in Minutes',
    workflowSub: 'Your team records in the field, you see it all in real-time.',
    steps: [
      { title: 'Field Inspection', desc: 'Beekeeper records hive status via mobile app.' },
      { title: 'Instant Sync', desc: 'Data syncs in real-time to your dashboard.' },
      { title: 'AI Analysis', desc: 'System flags issues and suggests actions.' },
      { title: 'Smart Reports', desc: 'Generate ELAP and management reports.' },
    ],
    ctaTitle: 'Ready to modernize your operation?',
    ctaSub: 'Join beekeepers who manage smarter with Hivenetic.',
    ctaBtn: 'Sign In to Dashboard',
    footerTagline: '© 2025 Hivenetic · Smart Beekeeping Management',
    loginBtn: 'Sign In',
    getStarted: 'Get Started',
  },
  he: {
    badge: '🐝 פלטפורמת גידול דבורים חכמה',
    heroTitle: 'נהל את הכוורות שלך ',
    heroTitleHighlight: 'בצורה החכמה',
    heroSub: 'ניטור בזמן אמת, המלצות מבוססות AI וניהול מלא של הכוורת — הכל במקום אחד.',
    cta: 'כניסה למערכת',
    statsLabel1: 'כוורות במעקב',
    statsLabel2: 'משתמשים פעילים',
    statsLabel3: 'בדיקות שבוצעו',
    statsLabel4: 'קציר דבש במעקב',
    statsValue4: '1M ק"ג',
    featuresLabel: 'כל מה שצריך',
    featuresTitle: 'נבנה עבור מגדלי דבורים מקצועיים',
    featuresSub: 'מכוורת אחת ועד מאות נקודות — Hivenetic גדל יחד איתך.',
    features: [
      { icon: '🏡', title: 'ניהול נקודות', desc: 'עקוב אחר כל מיקום עם קואורדינטות GPS, קוד שער והיסטוריית ביקורים.' },
      { icon: '🐝', title: 'ניטור כוורות', desc: 'רשום בדיקות, בריאות המושבה, נתוני אוכלוסייה ושיעורי תמותה לכל כוורת.' },
      { icon: '👥', title: 'תיאום צוות', desc: 'הקצה משימות לצוותי שטח, עקוב אחר התקדמות ונהל לוחות זמנים בקלות.' },
      { icon: '🍯', title: 'קציר ומלאי', desc: 'תעד חליבת דבש לפי סוג, נהל מלאי מחסן וארגזים.' },
      { icon: '🤖', title: 'המלצות AI', desc: 'קבל הצעות פעולה חכמות בהתבסס על נתוני בדיקות ודפוסים עונתיים.' },
      { icon: '📋', title: 'דוחות ELAP', desc: 'הפק דוחות ELAP לאירועי אובדן והערכות זכאות אוטומטית.' },
    ],
    workflowLabel: 'איך זה עובד',
    workflowTitle: 'מהשטח ללוח הבקרה תוך דקות',
    workflowSub: 'הצוות שלך מתעד בשטח, אתה רואה הכל בזמן אמת.',
    steps: [
      { title: 'בדיקת שטח', desc: 'הכוורן מתעד מצב כוורת דרך האפליקציה הניידת.' },
      { title: 'סנכרון מיידי', desc: 'הנתונים מסתנכרנים בזמן אמת ללוח הבקרה.' },
      { title: 'ניתוח AI', desc: 'המערכת מאתרת בעיות ומציעה פעולות.' },
      { title: 'דוחות חכמים', desc: 'הפק דוחות ELAP וניהול בקלות.' },
    ],
    ctaTitle: 'מוכן לשדרג את הניהול שלך?',
    ctaSub: 'הצטרף למגדלי הדבורים שמנהלים חכם יותר עם Hivenetic.',
    ctaBtn: 'כניסה ללוח הבקרה',
    footerTagline: '© 2025 Hivenetic · ניהול כוורות חכם',
    loginBtn: 'התחברות',
    getStarted: 'התחל',
  },
  ar: {
    badge: '🐝 منصة تربية النحل الذكية',
    heroTitle: 'أدر منحلك ',
    heroTitleHighlight: 'بالطريقة الذكية',
    heroSub: 'مراقبة فورية، توصيات مدعومة بالذكاء الاصطناعي، وإدارة كاملة للخلايا — كل شيء في مكان واحد.',
    cta: 'دخول النظام',
    statsLabel1: 'خلايا مُتتبَّعة',
    statsLabel2: 'مستخدمون نشطون',
    statsLabel3: 'فحوصات منجزة',
    statsLabel4: 'محاصيل العسل',
    statsValue4: '1M كغ',
    featuresLabel: 'كل ما تحتاجه',
    featuresTitle: 'مصمَّم لمربي النحل المحترفين',
    featuresSub: 'من خلية واحدة إلى مئات المناحل — Hivenetic ينمو معك.',
    features: [
      { icon: '🏡', title: 'إدارة المناحل', desc: 'تتبع كل موقع بإحداثيات GPS وأكواد البوابات وسجل الزيارات.' },
      { icon: '🐝', title: 'مراقبة الخلايا', desc: 'سجّل الفحوصات وصحة المستعمرة وإحصاءات السكان ومعدلات الوفيات لكل خلية.' },
      { icon: '👥', title: 'تنسيق الفريق', desc: 'وزّع المهام على فرق الحقل، وتابع التقدم، وأدر الجداول بسهولة.' },
      { icon: '🍯', title: 'الحصاد والمخزون', desc: 'سجّل استخلاص العسل حسب النوع وأدر مخزون المستودعات والصناديق.' },
      { icon: '🤖', title: 'توصيات AI', desc: 'احصل على اقتراحات ذكية بناءً على بيانات الفحص والأنماط الموسمية.' },
      { icon: '📋', title: 'تقارير ELAP', desc: 'أنشئ تقارير أحداث الخسارة وتقييمات الأهلية تلقائياً.' },
    ],
    workflowLabel: 'كيف يعمل',
    workflowTitle: 'من الحقل إلى لوحة التحكم في دقائق',
    workflowSub: 'فريقك يسجّل في الحقل، وأنت ترى كل شيء فوراً.',
    steps: [
      { title: 'فحص ميداني', desc: 'يسجّل النحّال حالة الخلية عبر تطبيق الجوال.' },
      { title: 'مزامنة فورية', desc: 'تتزامن البيانات فورياً مع لوحة التحكم.' },
      { title: 'تحليل AI', desc: 'يرصد النظام المشكلات ويقترح الإجراءات.' },
      { title: 'تقارير ذكية', desc: 'أنشئ تقارير ELAP والإدارة بسهولة.' },
    ],
    ctaTitle: 'هل أنت مستعد لتطوير عملياتك؟',
    ctaSub: 'انضم إلى مربّي النحل الذين يديرون بذكاء مع Hivenetic.',
    ctaBtn: 'دخول لوحة التحكم',
    footerTagline: '© 2025 Hivenetic · إدارة تربية النحل الذكية',
    loginBtn: 'تسجيل الدخول',
    getStarted: 'ابدأ',
  },
  es: {
    badge: '🐝 Plataforma Inteligente de Apicultura',
    heroTitle: 'Gestiona tus Apiarios ',
    heroTitleHighlight: 'de Forma Inteligente',
    heroSub: 'Monitoreo en tiempo real, recomendaciones con IA y gestión completa de colmenas — todo en una plataforma.',
    cta: 'Entrar al Sistema',
    statsLabel1: 'Colmenas Monitoreadas',
    statsLabel2: 'Usuarios Activos',
    statsLabel3: 'Inspecciones',
    statsLabel4: 'Cosecha Rastreada',
    statsValue4: '1M kg',
    featuresLabel: 'Todo lo que necesitas',
    featuresTitle: 'Creado para Apicultores Profesionales',
    featuresSub: 'Desde una sola colmena hasta cientos de apiarios — Hivenetic crece contigo.',
    features: [
      { icon: '🏡', title: 'Gestión de Apiarios', desc: 'Rastrea cada ubicación con coordenadas GPS, códigos de acceso e historial de visitas.' },
      { icon: '🐝', title: 'Monitoreo de Colmenas', desc: 'Registra inspecciones, salud de la colonia, estadísticas y tasas de mortalidad.' },
      { icon: '👥', title: 'Coordinación de Equipos', desc: 'Asigna tareas a equipos de campo, sigue el progreso y gestiona horarios fácilmente.' },
      { icon: '🍯', title: 'Cosecha e Inventario', desc: 'Registra extracciones de miel por tipo y gestiona el inventario de almacén.' },
      { icon: '🤖', title: 'Recomendaciones IA', desc: 'Obtén sugerencias inteligentes basadas en datos de inspección y patrones estacionales.' },
      { icon: '📋', title: 'Reportes ELAP', desc: 'Genera reportes de pérdida ELAP y evaluaciones de elegibilidad automáticamente.' },
    ],
    workflowLabel: 'Cómo funciona',
    workflowTitle: 'Del Campo al Panel en Minutos',
    workflowSub: 'Tu equipo registra en el campo, tú lo ves todo en tiempo real.',
    steps: [
      { title: 'Inspección en Campo', desc: 'El apicultor registra el estado de la colmena via app móvil.' },
      { title: 'Sincronización Inmediata', desc: 'Los datos se sincronizan en tiempo real al panel.' },
      { title: 'Análisis IA', desc: 'El sistema detecta problemas y sugiere acciones.' },
      { title: 'Reportes Inteligentes', desc: 'Genera reportes ELAP y de gestión fácilmente.' },
    ],
    ctaTitle: '¿Listo para modernizar tu operación?',
    ctaSub: 'Únete a los apicultores que gestionan con mayor inteligencia con Hivenetic.',
    ctaBtn: 'Entrar al Panel',
    footerTagline: '© 2025 Hivenetic · Gestión Inteligente de Apicultura',
    loginBtn: 'Iniciar Sesión',
    getStarted: 'Comenzar',
  },
  th: {
    badge: '🐝 แพลตฟอร์มการเลี้ยงผึ้งอัจฉริยะ',
    heroTitle: 'จัดการฟาร์มผึ้งของคุณ ',
    heroTitleHighlight: 'อย่างชาญฉลาด',
    heroSub: 'ติดตามแบบเรียลไทม์ คำแนะนำจาก AI และการจัดการรังผึ้งที่สมบูรณ์ — ทุกอย่างในที่เดียว',
    cta: 'เข้าสู่ระบบ',
    statsLabel1: 'รังผึ้งที่ติดตาม',
    statsLabel2: 'ผู้ใช้งานที่ใช้งาน',
    statsLabel3: 'การตรวจสอบ',
    statsLabel4: 'การเก็บเกี่ยวน้ำผึ้ง',
    statsValue4: '1M กก.',
    featuresLabel: 'ทุกสิ่งที่คุณต้องการ',
    featuresTitle: 'สร้างสำหรับผู้เลี้ยงผึ้งมืออาชีพ',
    featuresSub: 'จากรังผึ้งเดียวถึงหลายร้อยสวนผึ้ง — Hivenetic เติบโตไปพร้อมกับคุณ',
    features: [
      { icon: '🏡', title: 'การจัดการสวน', desc: 'ติดตามทุกที่ตั้งด้วยพิกัด GPS รหัสประตู และประวัติการเยี่ยมชม' },
      { icon: '🐝', title: 'ติดตามรังผึ้ง', desc: 'บันทึกการตรวจสอบ สุขภาพของอาณานิคม สถิติ และอัตราการตาย' },
      { icon: '👥', title: 'ประสานงานทีม', desc: 'มอบหมายงานให้ทีมภาคสนาม ติดตามความคืบหน้า และจัดการตารางเวลา' },
      { icon: '🍯', title: 'การเก็บเกี่ยวและสินค้าคงคลัง', desc: 'บันทึกการสกัดน้ำผึ้งตามประเภท และจัดการสต็อกคลังสินค้า' },
      { icon: '🤖', title: 'คำแนะนำ AI', desc: 'รับข้อเสนอแนะอัจฉริยะจากข้อมูลการตรวจสอบและรูปแบบตามฤดูกาล' },
      { icon: '📋', title: 'รายงาน ELAP', desc: 'สร้างรายงานเหตุการณ์สูญเสีย ELAP และการประเมินสิทธิ์โดยอัตโนมัติ' },
    ],
    workflowLabel: 'วิธีการทำงาน',
    workflowTitle: 'จากสนามสู่แดชบอร์ดในไม่กี่นาที',
    workflowSub: 'ทีมของคุณบันทึกในสนาม คุณเห็นทุกอย่างแบบเรียลไทม์',
    steps: [
      { title: 'ตรวจสอบภาคสนาม', desc: 'ผู้เลี้ยงผึ้งบันทึกสถานะรังผึ้งผ่านแอปมือถือ' },
      { title: 'ซิงค์ทันที', desc: 'ข้อมูลซิงค์แบบเรียลไทม์ไปยังแดชบอร์ด' },
      { title: 'วิเคราะห์ด้วย AI', desc: 'ระบบตรวจจับปัญหาและแนะนำการดำเนินการ' },
      { title: 'รายงานอัจฉริยะ', desc: 'สร้างรายงาน ELAP และการจัดการได้ง่าย' },
    ],
    ctaTitle: 'พร้อมที่จะปรับปรุงการดำเนินงานของคุณหรือยัง?',
    ctaSub: 'เข้าร่วมกับผู้เลี้ยงผึ้งที่จัดการอย่างชาญฉลาดกับ Hivenetic',
    ctaBtn: 'เข้าสู่แดชบอร์ด',
    footerTagline: '© 2025 Hivenetic · การจัดการการเลี้ยงผึ้งอัจฉริยะ',
    loginBtn: 'เข้าสู่ระบบ',
    getStarted: 'เริ่มต้น',
  },
}

const STATS: { number: string | ((c: LandingContent) => string); key: keyof LandingContent }[] = [
  { number: '10K+', key: 'statsLabel1' },
  { number: '500+', key: 'statsLabel2' },
  { number: '250K+', key: 'statsLabel3' },
  { number: c => c.statsValue4, key: 'statsLabel4' },
]

// ─── Component ────────────────────────────────────────────────────────────────

export default function LandingScreen(): React.JSX.Element {
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const { setLanguage } = useSettings()
  const initialLang = (i18n.language || 'en').split('-')[0]
  const [lang, setLang] = useState(initialLang in CONTENT ? initialLang : 'en')

  const c = CONTENT[lang] ?? CONTENT['en']
  const isRTL = LANGUAGES.find(l => l.code === lang)?.rtl ?? false

  const handleLangChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLang = e.target.value
    setLang(newLang)
    // Persist + apply RTL so Login/Register keep the chosen language and direction
    setLanguage(newLang as Parameters<typeof setLanguage>[0])
  }

  const goToLogin = () => navigate('/login')
  const goToRegister = () => navigate('/register')

  return (
    <Page $rtl={isRTL}>
      {/* ── Navbar ── */}
      <Nav>
        <NavLogo>
          <HexLogo src={logoMark} alt="Hivenetic" />
          Hivenetic
        </NavLogo>
        <NavRight>
          <LangSelect value={lang} onChange={handleLangChange}>
            {LANGUAGES.map(l => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </LangSelect>
          <NavGetStartedBtn onClick={goToRegister}>{c.getStarted}</NavGetStartedBtn>
          <NavLoginBtn onClick={goToLogin}>{c.loginBtn}</NavLoginBtn>
        </NavRight>
      </Nav>

      {/* ── Hero ── */}
      <Hero>
        <HeroBadge>{c.badge}</HeroBadge>
        <HeroTitle>
          {c.heroTitle}
          <span>{c.heroTitleHighlight}</span>
        </HeroTitle>
        <HeroSub>{c.heroSub}</HeroSub>
        <HeroCTA onClick={goToLogin}>{c.cta}</HeroCTA>
        <HeroEmoji>🍯</HeroEmoji>
      </Hero>

      {/* ── Stats ── */}
      <StatsBar>
        {STATS.map(s => (
          <StatItem key={s.key}>
            <StatNumber>{typeof s.number === 'function' ? s.number(c) : s.number}</StatNumber>
            <StatLabel>{c[s.key] as string}</StatLabel>
          </StatItem>
        ))}
      </StatsBar>

      {/* ── Features ── */}
      <FeaturesSection>
        <SectionLabel>{c.featuresLabel}</SectionLabel>
        <SectionTitle>{c.featuresTitle}</SectionTitle>
        <SectionSub>{c.featuresSub}</SectionSub>
        <FeaturesGrid>
          {c.features.map((f, i) => (
            <FeatureCard key={i}>
              <FeatureIcon>{f.icon}</FeatureIcon>
              <FeatureTitle>{f.title}</FeatureTitle>
              <FeatureDesc>{f.desc}</FeatureDesc>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </FeaturesSection>

      {/* ── Workflow ── */}
      <WorkflowSection>
        <WorkflowInner>
          <SectionLabel>{c.workflowLabel}</SectionLabel>
          <SectionTitle>{c.workflowTitle}</SectionTitle>
          <SectionSub>{c.workflowSub}</SectionSub>
          <StepsGrid>
            {c.steps.map((s, i) => (
              <StepCard key={i}>
                <StepNum>{i + 1}</StepNum>
                <StepTitle>{s.title}</StepTitle>
                <StepDesc>{s.desc}</StepDesc>
              </StepCard>
            ))}
          </StepsGrid>
        </WorkflowInner>
      </WorkflowSection>

      {/* ── CTA ── */}
      <CTASection>
        <CTATitle>{c.ctaTitle}</CTATitle>
        <CTASub>{c.ctaSub}</CTASub>
        <HeroCTA onClick={goToLogin}>{c.ctaBtn}</HeroCTA>
      </CTASection>

      {/* ── Footer ── */}
      <Footer>
        <NavLogo style={{ fontSize: 14 }}>
          <span style={{ width: 28, height: 28, fontSize: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>🐝</span>
          Hivenetic
        </NavLogo>
        <span>{c.footerTagline}</span>
      </Footer>
    </Page>
  )
}
